import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import useAuthStore from '../store/useAuthStore';
import { passwordValidationRule } from '../utils/passwordValidation';

const ResetPasswordPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { uidb64, token } = useParams();
  const navigate = useNavigate();
  const { resetPasswordConfirm, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const password = watch('new_password1');

  // Clear any existing errors when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await resetPasswordConfirm({
        uid: uidb64,
        token: token,
        new_password1: data.new_password1,
        new_password2: data.new_password2
      });
      toast.success('Password reset successful! You can now login with your new password.');
      navigate('/login');
    } catch (error) {
      console.error('Password reset confirmation error:', error);
      let errorMessage = 'Failed to reset password';
      
      if (error && typeof error === 'object') {
        if (error.message) {
          errorMessage = error.message;
        } else if (error.detail) {
          errorMessage = error.detail;
        } else if (error.non_field_errors && Array.isArray(error.non_field_errors)) {
          errorMessage = error.non_field_errors[0];
        } else if (error.new_password1 && Array.isArray(error.new_password1)) {
          errorMessage = error.new_password1[0];
        } else if (error.new_password2 && Array.isArray(error.new_password2)) {
          errorMessage = error.new_password2[0];
        } else if (error.token && Array.isArray(error.token)) {
          errorMessage = 'Invalid or expired reset link. Please request a new password reset.';
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md animate-fade-in">
        <div className="text-center">
          <h2 className="mt-8 text-center text-2xl md:text-3xl font-extrabold  dark:text-white animate-slide-up">
            Set New Password
          </h2>
          <p className="mt-4 text-center text-base text-white dark:text-white leading-relaxed animate-slide-up animation-delay-100">
            Enter your new password below to complete the reset process.
          </p>
        </div>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md animate-slide-up animation-delay-200">
        <div className="card card-body shadow-xl border-0 backdrop-blur-sm bg-white/95 dark:bg-gray-800/95">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <div>
              <label className="block text-sm font-semibold text-white dark:text-white mb-4">
                New Password
              </label>
              <div className="relative">
                <input
                  {...register('new_password1', {
                    required: 'Password is required',
                    validate: passwordValidationRule
                  })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="w-full h-12 px-4 pr-12 text-lg border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-all duration-200 hover:border-primary-400 dark:hover:border-primary-400 focus:scale-[1.02] focus:shadow-lg"
                  placeholder="Enter your new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-white hover:text-white dark:hover:text-white transition-colors" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-white hover:text-white dark:hover:text-white transition-colors" />
                  )}
                </button>
                {errors.new_password1 && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {errors.new_password1.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white dark:text-white mb-4">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  {...register('new_password2', {
                    required: 'Please confirm your password',
                    validate: (value) => value === password || 'Passwords do not match'
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="w-full h-12 px-4 pr-12 text-lg border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-all duration-200 hover:border-primary-400 dark:hover:border-primary-400 focus:scale-[1.02] focus:shadow-lg"
                  placeholder="Confirm your new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-white hover:text-white dark:hover:text-white transition-colors" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-white hover:text-white dark:hover:text-white transition-colors" />
                  )}
                </button>
                {errors.new_password2 && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {errors.new_password2.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full h-12 px-6 py-3 font-semibold rounded-xl shadow-lg transition-all duration-200 flex justify-center items-center ${
                  isLoading
                    ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-white cursor-not-allowed shadow-none transform-none'
                    : 'bg-[#0E1696] hover:from-primary-600 hover:to-primary-700 !text-white hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="loading-spinner white mr-3 w-5 h-5"></div>
                    Updating Password...
                  </>
                ) : (
                  'Update Password'
                )}
              </button>
            </div>

            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-6">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm font-medium text-white dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Login
                </Link>
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center text-sm font-medium text-white dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Request New Reset Link
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
